/**
 * Excel + ZIP import (specs/dataset-workspace.md, #796): upload the source list and the
 * PDFs, wait while they are read and matched, then match the rest in MatchingView.
 * The alternative to uploading PDFs alone (#891): its form stays folded until opened.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FileSpreadsheet, Loader } from 'lucide-react';
import { Label, Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { useConfirm } from '../../../components/DialogProvider';
import { getCurrentImport, startImport, cancelImport } from './importApi';
import MatchingView from './MatchingView';
import { Section, SectionTitle, Lead, FieldRow, ErrorText } from './styles';

const POLL_MS = 3000;

function UploadForm({ datasetId, onStarted }) {
  const { t } = useTranslation();
  const formRef = useRef(null);
  const [sources, setSources] = useState(null);
  const [archive, setArchive] = useState(null);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const imp = await startImport(datasetId, { sources, archive, files }, {
        onProgress: ({ loaded, total }) => setProgress(Math.round((loaded / total) * 100)),
      });
      formRef.current?.reset();
      onStarted(imp);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit}>
      <FieldRow>
        <div>
          <Label htmlFor="imp-sources">{t('datasetImport.sourcesLabel')}</Label>
          <input id="imp-sources" type="file" accept=".xlsx,.csv" required style={{ color: '#ccc' }}
            onChange={(e) => setSources(e.target.files?.[0] || null)} />
        </div>
        <div>
          <Label htmlFor="imp-archive">{t('datasetImport.archiveLabel')}</Label>
          <input id="imp-archive" type="file" accept=".zip,application/zip" style={{ color: '#ccc' }}
            onChange={(e) => setArchive(e.target.files?.[0] || null)} />
        </div>
        <div>
          <Label htmlFor="imp-files">{t('datasetImport.filesLabel')}</Label>
          <input id="imp-files" type="file" accept=".pdf,application/pdf" multiple style={{ color: '#ccc' }}
            onChange={(e) => setFiles([...(e.target.files || [])])} />
        </div>
      </FieldRow>
      <Actions>
        <Button type="submit" $variant="primary" disabled={!sources || busy}>
          {busy
            ? `${t('datasetImport.uploading')}${progress != null ? ` ${progress} %` : ''}`
            : t('datasetImport.start')}
        </Button>
        {error && <StatusLine $error>{error}</StatusLine>}
      </Actions>
    </form>
  );
}

export default function ImportPanel({ datasetId, onConfirmed }) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [imp, setImp] = useState(undefined); // undefined = loading, null = none open
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setImp(await getCurrentImport(datasetId));
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [datasetId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (imp?.status !== 'extracting') return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [imp?.status, load]);

  const handleCancel = async () => {
    if (!(await confirm({ message: t('datasetImport.confirmCancel'), destructive: true }))) return;
    try {
      await cancelImport(datasetId, imp.id);
      setImp(null);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Section>
      <SectionTitle><FileSpreadsheet size={18} /> {t('datasetImport.title')}</SectionTitle>
      {error && <ErrorText>{error}</ErrorText>}
      {imp === null && !open && (
        <Actions>
          <Lead style={{ margin: 0 }}>{t('datasetImport.alternative')}</Lead>
          <Button $size="small" onClick={() => setOpen(true)}>{t('datasetImport.openForm')}</Button>
        </Actions>
      )}
      {imp === null && open && (
        <>
          <Lead>{t('datasetImport.lead')}</Lead>
          <UploadForm datasetId={datasetId} onStarted={setImp} />
        </>
      )}
      {imp?.status === 'extracting' && (
        <Lead style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader size={14} /> {t('datasetImport.extracting')}
        </Lead>
      )}
      {imp?.status === 'failed' && (
        <>
          <ErrorText>{t('datasetImport.failed')}: {imp.error}</ErrorText>
          <Actions><Button $size="small" onClick={handleCancel}>{t('datasetImport.cancel')}</Button></Actions>
        </>
      )}
      {imp?.status === 'review' && (
        <MatchingView
          datasetId={datasetId}
          imp={imp}
          onChange={setImp}
          onCancel={handleCancel}
          onConfirmed={() => { setImp(null); onConfirmed(); }}
        />
      )}
    </Section>
  );
}
